import { supabase } from "@/integrations/supabase/client";

/**
 * Resolución segura del rol de Super Admin y del destino post-login.
 *
 * - El rol se resuelve SIEMPRE en servidor con el RPC canónico `is_user_admin`
 *   (SECURITY DEFINER sobre `public.user_roles`, mismo que usan las Edge
 *   Functions admin-*). No se leen tablas de roles desde el cliente ni se
 *   confía en localStorage.
 * - No se usa `verify_admin_session_secure` para el enrutado porque ese RPC
 *   registra un evento `unauthorized_admin_access_attempt` cada vez que lo
 *   invoca un usuario normal; llamarlo en cada login de cliente ensuciaría la
 *   auditoría. Sigue siendo la puerta de `/admin` (AdminDashboard).
 */

export const ADMIN_HOME = "/admin";
export const CLIENT_HOME = "/dashboard";

/**
 * Un redirect explícito solo se respeta si es una ruta interna de la app:
 * empieza por "/", no es protocol-relative ("//"), no contiene esquema ni
 * saltos de línea, y no apunta de vuelta a /auth (evita bucles).
 */
export function isSafeRedirectPath(value: string | null | undefined): value is string {
  if (!value) return false;
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.startsWith("/\\")) return false;
  if (/[\r\n]/.test(value)) return false;
  if (/^\/auth(?:[/?#]|$)/i.test(value)) return false;
  return true;
}

/**
 * Intención de compra pública (P2-BILLING-5).
 * Sólo se aceptan los planes públicos vigentes.
 */
export type PurchasePlan = "basic" | "premium" | "enterprise";
export type PurchasePeriod = "monthly" | "annual";

export function parsePurchasePlan(value: string | null | undefined): PurchasePlan | null {
  return value === "basic" || value === "premium" || value === "enterprise" ? value : null;
}

export function parsePurchasePeriod(value: string | null | undefined): PurchasePeriod | null {
  return value === "monthly" || value === "annual" ? value : null;
}

/** Query string validado para Vivo/Impulso/Control o cadena vacía. */
export function buildPurchaseIntentQuery(
  plan: string | null | undefined,
  period: string | null | undefined,
): string {
  const safePlan = parsePurchasePlan(plan);
  if (!safePlan) return "";
  const safePeriod = parsePurchasePeriod(period) ?? "monthly";
  return `?plan=${safePlan}&period=${safePeriod}`;
}

export interface DestinationInput {
  isAdmin: boolean;
  redirect?: string | null;
  plan?: string | null;
  period?: string | null;
}

/**
 * Regla única de destino post-autenticación:
 *   1. redirect seguro y explícito → redirect (la ruta destino aplica su propia
 *      autorización: HRRouteGuard en /rh, verify_admin_session_secure en /admin)
 *   2. admin → /admin
 *   3. cualquier otro → /dashboard
 */
export function resolvePostAuthDestination({
  isAdmin,
  redirect,
  plan,
  period,
}: DestinationInput): string {
  if (isSafeRedirectPath(redirect)) return redirect;
  if (isAdmin) return ADMIN_HOME;
  return `${CLIENT_HOME}${buildPurchaseIntentQuery(plan, period)}`;
}

/**
 * Consulta en servidor si `userId` es admin de plataforma. Cualquier error de
 * red o de permisos se trata como "no admin" para no bloquear el acceso normal.
 */
export async function resolveIsAdmin(userId: string | null | undefined): Promise<boolean> {
  if (!userId) return false;
  try {
    const { data, error } = await supabase.rpc("is_user_admin", { check_user_id: userId });
    if (error) {
      console.warn("[adminRole] is_user_admin failed:", error.message);
      return false;
    }
    return data === true;
  } catch (err) {
    console.warn("[adminRole] is_user_admin threw:", err);
    return false;
  }
}
